import type { MCPGatewaySyncState } from '@kirin/api-client/contracts/mcp';

/**
 * Applied when the gateway's appliedPolicyVersion has caught up to the
 * version stamped at the row's (server or tool) last toggle. A row that was
 * never toggled has no policyVersion and is trivially "applied" — there is
 * nothing pending for it.
 */
export function isPolicyApplied(
  policyVersion: number | null | undefined,
  appliedPolicyVersion: number | null | undefined
): boolean {
  if (policyVersion == null) return true;
  if (appliedPolicyVersion == null) return false;
  return appliedPolicyVersion >= policyVersion;
}

/** Shown before the table's first `onMeta` fires — reads as "never synced". */
export const DEFAULT_GATEWAY_SYNC_STATE: MCPGatewaySyncState = {
  lastContactAt: null,
  policyVersion: null,
  appliedPolicyVersion: null,
  gatewayCount: 0,
};

export type BlockConfirmTarget =
  | { kind: 'server'; serverId: string; serverName: string; toolCount: number }
  | { kind: 'tool'; serverId: string; toolId: string; toolName: string }
  | {
      /**
       * An Inventory server discovered on endpoints (KIR-4482): blocked by
       * the endpoint hook rather than by a gateway.
       */
      kind: 'endpointServer';
      serverName: string;
      /** Assistants seen with this server whose hook enforces the block. */
      enforcedAssistants: string[];
      /** Assistants seen with this server that are not enforced. */
      notEnforcedAssistants: string[];
      /** Users with the server configured, across every assistant. */
      userCount: number;
      /** KIR-4701: the security group the block is for; absent org-wide. */
      groupName?: string;
      /**
       * KIR-4701: the scope the dialog was opened in (`null` org-wide).
       * Confirm writes only while the live scope still matches it.
       */
      securityGroupId: string | null;
    };

export interface GatewayControlCallbacks {
  appliedPolicyVersion: number | null;
  onAllowServer: (serverId: string) => void;
  onAllowTool: (serverId: string, toolId: string) => void;
  onRequestBlockConfirmation: (target: BlockConfirmTarget) => void;
}
